/**
 * A date field: a select-style trigger that opens a picker. On a phone the
 * picker is a bottom sheet of three scroll wheels (day · month · year); above
 * 768px it is a calendar popover — shadcn's date picker, i.e. react-day-picker
 * with month and year dropdowns, on this app's own styles (./DateCalendar).
 * Which days exist is the caller's `range` (./dateField: `birthDateRange`,
 * `expiryDateRange`, `pastDateRange`), and both pickers read it.
 *
 * The browser's own picker is what both replace: Android opens a month calendar
 * at TODAY, so reaching a birth year means paging back ~360 months; iOS's inline
 * wheel has no year jump; Chrome's desktop popup opens on today as well, in the
 * browser's look rather than the product's. The wheels take one flick each; the
 * calendar opens on the chosen month — or the range's `opensAt` — with any year
 * one dropdown away.
 *
 * The sheet writes nothing until "Done": it opens on a draft (the current value,
 * or `opensAt` when empty) and closing it any other way leaves the field
 * untouched, so the form never asserts a date the applicant did not confirm.
 * The calendar writes on the day picked — picking a day is the confirmation, as
 * in any date picker — and closing it otherwise writes nothing.
 *
 * Each wheel is a `role="spinbutton"` (arrows, Home/End) over a CSS scroll-snap
 * column; the value is `round(scrollTop / ROW)` once the scroll settles.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  type DateRange,
  type Ymd,
  calendarFor,
  clampYmd,
  daysInMonth,
  parseIso,
  toIso,
  yearFormat,
} from './dateField';
import { Chevrons } from './CountryPicker';
import { loadDateCalendar, loadedDateCalendar } from './dateCalendarChunk';
import { useNarrowViewport } from './viewport';

/** Row height in px — must equal `.date-wheel__item` height in theme.css. */
const ROW = 40;

/** `first`..`last`, inclusive. */
const span = (first: number, last: number) =>
  Array.from({ length: Math.max(0, last - first + 1) }, (_, i) => first + i);

export interface DateInputProps {
  /** ISO `YYYY-MM-DD`, or '' when nothing is chosen yet. */
  value: string;
  onChange: (iso: string) => void;
  /** Accessible name, and the heading of the sheet and the calendar. */
  label: string;
  placeholder: string;
  /** The days that exist for this field, and where it opens when empty. */
  range: DateRange;
  name?: string;
}

export function DateInput({ value, onChange, label, placeholder, range, name }: DateInputProps) {
  const { t, i18n } = useTranslation();
  const asSheet = useNarrowViewport();
  const [open, setOpen] = useState(false);
  // Opened from the keyboard (Enter/Space click with `detail` 0): focus moves
  // into the calendar. From a pointer it stays on the field — Chrome counts
  // script focus after a click as :focus-visible and would ring the month for
  // nothing; Tab still enters the calendar next.
  const [keyboardOpened, setKeyboardOpened] = useState(false);
  const field = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [chunk, setChunk] = useState(loadedDateCalendar);
  const chosen = parseIso(value);
  const calendar = calendarFor(i18n.language);
  const shown = chosen
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeZone: 'UTC', calendar }).format(
        Date.UTC(chosen.year, chosen.month - 1, chosen.day),
      )
    : null;

  const commit = (ymd: Ymd) => {
    onChange(toIso(ymd));
    setOpen(false);
  };
  // Back to the field after Escape or a pick, as a popover hands focus back;
  // not after a tap elsewhere, which already put focus where it was aimed.
  const dismiss = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  }, []);

  const preload = useCallback(() => {
    if (asSheet || chunk) return;
    loadDateCalendar().then(setChunk, () => {
      // Retried on the next hover, focus or open; nothing to tell the applicant yet.
    });
  }, [asSheet, chunk]);
  // Opened before a preload landed (a click with no hover first): fetch now.
  useEffect(() => {
    if (open) preload();
  }, [open, preload]);

  return (
    <div className="date-field" ref={field}>
      <button
        ref={trigger}
        type="button"
        name={name}
        className={`details-input country-select__trigger${chosen ? '' : ' is-empty'}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={shown ? `${label}: ${shown}` : label}
        data-track="date_field"
        onPointerEnter={preload}
        onFocus={preload}
        onClick={(event) => {
          setKeyboardOpened(event.detail === 0);
          setOpen((was) => !was);
        }}
      >
        <span className="country-select__value">{shown ?? placeholder}</span>
        {asSheet ? <Chevrons /> : <CalendarIcon />}
      </button>
      {open && asSheet ? (
        <DateSheet
          title={label}
          initial={chosen}
          range={range}
          doneLabel={t('flow.datePicker.done')}
          closeLabel={t('flow.countryPicker.close')}
          onClose={() => setOpen(false)}
          onDone={commit}
        />
      ) : null}
      {open && !asSheet && chunk ? (
        <chunk.DateCalendar
          field={field}
          label={label}
          initial={chosen}
          range={range}
          takeFocus={keyboardOpened}
          onClose={dismiss}
          onPick={(ymd) => {
            commit(ymd);
            trigger.current?.focus();
          }}
        />
      ) : null}
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

interface DateSheetProps {
  title: string;
  initial: Ymd | null;
  range: DateRange;
  doneLabel: string;
  closeLabel: string;
  onClose: () => void;
  onDone: (value: Ymd) => void;
}

function DateSheet({ title, initial, range, doneLabel, closeLabel, onClose, onDone }: DateSheetProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { min, max } = range;
  const [draft, setDraft] = useState<Ymd>(() => initial ?? range.opensAt);
  const panel = useRef<HTMLDivElement>(null);

  // Any edit goes through the clamp, so changing month or year can never leave
  // the day wheel on the 31st of a 30-day month, or outside the range.
  const set = (patch: Partial<Ymd>) => setDraft((d) => clampYmd({ ...d, ...patch }, range));

  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  const days = useMemo(() => {
    const first = draft.year === min.year && draft.month === min.month ? min.day : 1;
    const last =
      draft.year === max.year && draft.month === max.month
        ? max.day
        : daysInMonth(draft.year, draft.month);
    return span(first, last);
  }, [draft.year, draft.month, min, max]);
  const months = useMemo(() => {
    const first = draft.year === min.year ? min.month : 1;
    const last = draft.year === max.year ? max.month : 12;
    const name = new Intl.DateTimeFormat(language, { month: 'long', timeZone: 'UTC' });
    return span(first, last).map((m) => ({ value: m, text: name.format(Date.UTC(2000, m - 1, 1)) }));
  }, [draft.year, min, max, language]);
  const years = useMemo(() => {
    const text = yearFormat(language);
    return span(min.year, max.year).map((y) => ({ value: y, text: text(y) }));
  }, [min.year, max.year, language]);

  return createPortal(
    <div className="country-sheet">
      <div className="country-sheet__backdrop" aria-hidden="true" />
      <div ref={panel} className="country-sheet__panel" role="dialog" aria-modal="true" aria-label={title}>
        <div className="country-sheet__handle" aria-hidden="true" />
        <div className="country-sheet__header">
          <h2 className="country-sheet__title">{title}</h2>
          <button type="button" className="country-sheet__close" aria-label={closeLabel} data-track="close_picker" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
        <div className="date-wheels">
          <div className="date-wheels__band" aria-hidden="true" />
          <Wheel
            label={t('flow.datePicker.day')}
            items={days.map((d) => ({ value: d, text: String(d) }))}
            value={draft.day}
            onValue={(day) => set({ day })}
            narrow
          />
          <Wheel
            label={t('flow.datePicker.month')}
            items={months}
            value={draft.month}
            onValue={(month) => set({ month })}
          />
          <Wheel
            label={t('flow.datePicker.year')}
            items={years}
            value={draft.year}
            onValue={(year) => set({ year })}
            narrow
          />
        </div>
        <div className="date-sheet__actions">
          <button type="button" className="btn" data-track="confirm_date" onClick={() => onDone(draft)}>
            {doneLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface WheelProps {
  label: string;
  items: { value: number; text: string }[];
  value: number;
  onValue: (value: number) => void;
  narrow?: boolean;
}

function Wheel({ label, items, value, onValue, narrow }: WheelProps) {
  const column = useRef<HTMLDivElement>(null);
  const settle = useRef<number | undefined>(undefined);
  const placed = useRef(false);
  const index = Math.max(0, items.findIndex((item) => item.value === value));

  // Bring the column to the selected row: instantly on open, smoothly after
  // (a tap on a row, an arrow key, or a clamp when the month got shorter).
  useLayoutEffect(() => {
    const el = column.current;
    if (!el || Math.round(el.scrollTop / ROW) === index) return;
    if (placed.current && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: index * ROW, behavior: 'smooth' });
    } else {
      el.scrollTop = index * ROW;
    }
    placed.current = true;
  }, [index]);
  useEffect(() => () => window.clearTimeout(settle.current), []);

  const pick = (i: number) => {
    const item = items[Math.min(Math.max(i, 0), items.length - 1)];
    if (item && item.value !== value) onValue(item.value);
  };

  return (
    <div
      ref={column}
      className={`date-wheel${narrow ? ' date-wheel--narrow' : ''}`}
      role="spinbutton"
      tabIndex={0}
      aria-label={label}
      aria-valuenow={value}
      aria-valuetext={items[index]?.text ?? ''}
      aria-valuemin={items[0]?.value}
      aria-valuemax={items[items.length - 1]?.value}
      onScroll={(event) => {
        const top = event.currentTarget.scrollTop;
        window.clearTimeout(settle.current);
        settle.current = window.setTimeout(() => pick(Math.round(top / ROW)), 90);
      }}
      onKeyDown={(event) => {
        const step = { ArrowUp: -1, ArrowDown: 1 }[event.key];
        if (step !== undefined) pick(index + step);
        else if (event.key === 'Home') pick(0);
        else if (event.key === 'End') pick(items.length - 1);
        else return;
        event.preventDefault();
      }}
    >
      {items.map((item, i) => (
        <div
          key={item.value}
          className={`date-wheel__item${i === index ? ' is-selected' : ''}`}
          aria-hidden="true"
          onClick={() => pick(i)}
        >
          {item.text}
        </div>
      ))}
    </div>
  );
}
